import { MenuColumn } from './utils/types';
import { ListItem, ListItemButton, Stack } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { useLocation } from 'react-router';
import useSweepNavigate from '../../../navigation-utils/useSweepNavigate';
import { ACTIVE_COLOR, HOVER_COLOR } from './utils/colors';
import { readableUUID } from '../../../navigation-utils/readableUUID';
import { useSelector } from 'react-redux';
import { selectDefaultCreationEnvironment } from '../../pages/environments/environmentsReducer';
import { handleNavigationWithModifiers } from './utils/navigationUtils';
import { useCallback } from 'react';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';

interface DashboardSubmenuProps {
  menuColumns: MenuColumn[];
  onSelect: () => void;
  noPaddingBottom?: boolean;
}

export const DashboardSubmenu = ({
  menuColumns,
  onSelect,
  noPaddingBottom,
}: DashboardSubmenuProps) => {
  const { supportAgentInAgenticCenter } = useFeatureToggle();
  const { navigate } = useSweepNavigate();
  const pathname = useLocation().pathname;
  const defaultCreationEnv = useSelector(selectDefaultCreationEnvironment);
  const envIdSuffix = defaultCreationEnv ? `/${readableUUID(defaultCreationEnv.id)}` : '';

  const handleMenuItemClick = useCallback(
    (event: React.MouseEvent, item: any) => {
      event.stopPropagation();
      const routeTo = item.withEnvSuffix ? `${item.route}${envIdSuffix}` : item.route;
      handleNavigationWithModifiers(event, routeTo, () => {
        navigate(routeTo);
        onSelect();
      });
    },
    [envIdSuffix, navigate, onSelect],
  );

  return (
    <Stack direction="row">
      {menuColumns.map((column) => {
        return (
          <Stack
            key={column.columnId}
            sx={{
              '&:not(:last-child)': {
                borderRight: `1px solid ${colors.grey[300]}`,
              },
              minWidth: '188px',
              pb: noPaddingBottom ? 0 : 1,
              pt: column.columnLabel ? 0 : 1,
            }}
          >
            {column.columnLabel && (
              <Stack
                sx={{ p: '8px 16px', minWidth: '214px', height: '40px' }}
                justifyContent="center"
              >
                <Typography variant="caption" color={colors.grey[700]}>
                  {column.columnLabel}
                </Typography>
              </Stack>
            )}

            {column.menuItems.map((item) => {
              if (item.label === 'User Support Agent' && supportAgentInAgenticCenter) {
                return null;
              }

              const isActive = pathname.includes(item.route);
              const isSubMenu =
                !!item.subMenuId && item.subMenuItems && item.subMenuItems?.length > 0;

              if (isSubMenu) {
                return (
                  <DashboardSubmenu
                    key={item.subMenuId}
                    menuColumns={[
                      {
                        columnId: item.subMenuId ?? '',
                        columnLabel: item.subMenuId ?? '',
                        menuItems: item.subMenuItems ?? [],
                      },
                    ]}
                    onSelect={onSelect}
                    noPaddingBottom
                  />
                );
              }

              return (
                <ListItem key={item.label} disablePadding sx={{ m: 0 }}>
                  <ListItemButton
                    onClick={(event) => handleMenuItemClick(event, item)}
                    sx={{
                      p: '8px 16px',
                      m: 0,
                      background: isActive ? ACTIVE_COLOR : 'transparent',
                      '&:hover': {
                        background: isActive ? ACTIVE_COLOR : HOVER_COLOR,
                      },
                      gap: 1,
                      '& svg path': {
                        //force icon color
                        stroke: colors.blue[500],
                      },
                    }}
                  >
                    {item.icon}
                    <Typography variant="body">{item.label}</Typography>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Stack>
        );
      })}
    </Stack>
  );
};
